import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  CalendarIcon,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Booking() {
  const [step, setStep] = useState(1);
  const [taxServices, setTaxServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState({
    client_name: "",
    email: "",
    phone: "",
    service: "",
    time_slot: "",
    notes: "",
    honeypot: "", // Spam protection
  });

  const timeSlots = [
    "9:00 AM",
    "9:30 AM",
    "10:00 AM",
    "10:30 AM",
    "11:00 AM",
    "11:30 AM",
    "12:00 PM",
    "12:30 PM",
    "1:00 PM",
    "1:30 PM",
    "2:00 PM",
    "2:30 PM",
    "3:00 PM",
    "3:30 PM",
    "4:00 PM",
    "4:30 PM",
    "5:00 PM",
    "5:30 PM",
  ];

  useEffect(() => {
    const fetchTaxServices = async () => {
      try {
        const response = await axios.get(`${API}/tax-services`);
        setTaxServices(response.data);
      } catch (error) {
        console.error("Error fetching tax services:", error);
        // Fallback services
        setTaxServices([
          { id: "tax-preparation", name: "Tax Preparation", duration: 60 },
          { id: "tax-consultation", name: "Tax Consultation", duration: 30 },
          { id: "business-tax", name: "Business Tax", duration: 90 },
          { id: "other", name: "Other", duration: 30 },
        ]);
      }
    };
    fetchTaxServices();
  }, []);

  useEffect(() => {
    const fetchBookedSlots = async () => {
      if (selectedDate) {
        try {
          const dateStr = format(selectedDate, "yyyy-MM-dd");
          const response = await axios.get(
            `${API}/appointments/slots/${dateStr}`
          );
          setBookedSlots(response.data.booked_slots || []);
        } catch (error) {
          console.error("Error fetching booked slots:", error);
          setBookedSlots([]);
        }
      }
    };
    fetchBookedSlots();
  }, [selectedDate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleTimeSlotSelect = (slot) => {
    setFormData((prev) => ({ ...prev, time_slot: slot }));
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      return formData.service !== "";
    }
    if (currentStep === 2) {
      return selectedDate && formData.time_slot !== "";
    }
    if (currentStep === 3) {
      return (
        formData.client_name !== "" &&
        formData.email !== "" &&
        formData.phone !== ""
      );
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      toast.error("Please complete all required fields");
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep(3)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const appointmentData = {
        ...formData,
        date: format(selectedDate, "yyyy-MM-dd"),
      };

      await axios.post(`${API}/appointments`, appointmentData);
      setIsSuccess(true);
      toast.success("Appointment booked successfully! Check your email for confirmation.");
    } catch (error) {
      console.error("Error booking appointment:", error);
      const message = error.response?.data?.detail || "Failed to book appointment. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      email: "",
      phone: "",
      service: "",
      time_slot: "",
      notes: "",
      honeypot: "",
    });
    setSelectedDate(undefined);
    setIsSuccess(false);
    setStep(1);
  };

  if (isSuccess) {
    return (
      <div data-testid="booking-page" className="min-h-screen pt-20">
        <section className="py-20 md:py-32 bg-[#F9F9F7]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div
              data-testid="booking-success"
              className="bg-white rounded-2xl p-12 shadow-xl"
            >
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-[#0F392B] mb-4">
                Appointment Confirmed!
              </h2>
              <p className="font-body text-[#4A5568] mb-6">
                Your appointment has been successfully scheduled. We've sent a
                confirmation email to{" "}
                <span className="font-semibold">{formData.email}</span>.
              </p>
              <div className="bg-[#F9F9F7] rounded-xl p-6 mb-6 text-left">
                <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-4">
                  Appointment Details
                </h3>
                <p className="font-body text-[#4A5568] mb-2">
                  <strong>Service:</strong>{" "}
                  {taxServices.find((s) => s.id === formData.service)?.name ||
                    formData.service}
                </p>
                <p className="font-body text-[#4A5568] mb-2">
                  <strong>Date:</strong>{" "}
                  {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                </p>
                <p className="font-body text-[#4A5568] mb-2">
                  <strong>Time:</strong> {formData.time_slot}
                </p>
                <p className="font-body text-[#4A5568]">
                  <strong>Location:</strong> 1368-B S Military Trail, Suite R,
                  West Palm Beach, FL 33415
                </p>
              </div>
              <p className="font-body text-sm text-[#4A5568] mb-8">
                If you need to reschedule, please contact us at{" "}
                <a
                  href="tel:786-991-8919"
                  className="text-[#0F392B] font-semibold"
                >
                  786-991-8919
                </a>
              </p>
              <Button
                onClick={resetForm}
                data-testid="book-another-btn"
                className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-3"
              >
                Book Another Appointment
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-[#0F392B] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D4AF37] blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="font-accent text-sm text-[#D4AF37] tracking-widest mb-4 block">
              BOOK APPOINTMENT
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              Schedule Your Tax Consultation
            </h1>
            <p className="font-body text-white/80 max-w-xl mx-auto">
              Book your appointment in 3 easy steps. We'll send you a
              confirmation email.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                    s <= step
                      ? "bg-[#0F392B] text-white"
                      : "bg-stone-200 text-[#4A5568]"
                  }`}
                >
                  {s}
                </div>
                <span
                  className={`ml-2 font-body text-sm hidden sm:block ${
                    s <= step ? "text-[#0F392B]" : "text-[#A0AEC0]"
                  }`}
                >
                  {s === 1 ? "Select Service" : s === 2 ? "Choose Date & Time" : "Your Details"}
                </span>
                {s < 3 && (
                  <div
                    className={`w-12 h-0.5 mx-4 ${
                      s < step ? "bg-[#0F392B]" : "bg-stone-200"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit}>
            {/* Honeypot - hidden from users */}
            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleInputChange}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            {/* Step 1: Select Service */}
            {step === 1 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
                <h2 className="font-heading text-2xl font-semibold text-[#0F392B] mb-6">
                  Select Your Service
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {taxServices.map((service) => (
                    <button
                      key={service.id}
                      type="button"
                      onClick={() => handleServiceChange(service.id)}
                      data-testid={`service-option-${service.id}`}
                      className={`p-6 rounded-xl border-2 text-left transition-all duration-200 ${
                        formData.service === service.id
                          ? "border-[#0F392B] bg-[#0F392B]/5"
                          : "border-stone-200 hover:border-[#D4AF37]/50"
                      }`}
                    >
                      <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-1">
                        {service.name}
                      </h3>
                      <p className="font-body text-sm text-[#4A5568]">
                        Duration: ~{service.duration} minutes
                      </p>
                    </button>
                  ))}
                </div>
                <div className="mt-8 flex justify-end">
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!formData.service}
                    data-testid="next-step-btn"
                    className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-3"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Select Date & Time */}
            {step === 2 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
                <h2 className="font-heading text-2xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                  <CalendarIcon className="w-6 h-6 text-[#D4AF37]" />
                  Select Date & Time
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) =>
                        date < new Date() || date.getDay() === 0
                      }
                      className="rounded-lg border border-stone-200 p-4"
                      data-testid="booking-calendar"
                    />
                  </div>

                  {selectedDate && (
                    <div data-testid="time-slots-container">
                      <h3 className="font-body font-semibold text-[#0F392B] mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-[#D4AF37]" />
                        Available Times for {format(selectedDate, "MMM d")}
                      </h3>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((slot) => {
                          const isBooked = bookedSlots.includes(slot);
                          const isSelected = formData.time_slot === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              onClick={() =>
                                !isBooked && handleTimeSlotSelect(slot)
                              }
                              disabled={isBooked}
                              data-testid={`time-slot-${slot.replace(/\s+/g, "-").toLowerCase()}`}
                              className={`py-2.5 px-3 rounded-lg text-sm font-body transition-all duration-200 ${
                                isBooked
                                  ? "bg-stone-100 text-stone-400 cursor-not-allowed line-through"
                                  : isSelected
                                    ? "bg-[#0F392B] text-white"
                                    : "bg-stone-50 text-[#4A5568] hover:bg-[#D4AF37]/20 hover:text-[#0F392B]"
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex justify-between">
                  <Button
                    type="button"
                    onClick={prevStep}
                    variant="outline"
                    className="rounded-full px-6 py-3"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!selectedDate || !formData.time_slot}
                    data-testid="next-step-btn-2"
                    className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-3"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Your Details */}
            {step === 3 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg animate-fade-in">
                <h2 className="font-heading text-2xl font-semibold text-[#0F392B] mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-[#D4AF37]" />
                  Your Information
                </h2>

                <div className="space-y-6">
                  <div>
                    <Label
                      htmlFor="client_name"
                      className="font-body text-sm text-[#4A5568] mb-2 block"
                    >
                      Full Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                      <Input
                        id="client_name"
                        name="client_name"
                        value={formData.client_name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        data-testid="input-name"
                        className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="email"
                      className="font-body text-sm text-[#4A5568] mb-2 block"
                    >
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        required
                        data-testid="input-email"
                        className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="phone"
                      className="font-body text-sm text-[#4A5568] mb-2 block"
                    >
                      Phone Number *
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AEC0]" />
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        required
                        data-testid="input-phone"
                        className="pl-10 h-12 border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="notes"
                      className="font-body text-sm text-[#4A5568] mb-2 block"
                    >
                      Additional Notes (Optional)
                    </Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Any specific requirements or questions..."
                      rows={4}
                      data-testid="input-notes"
                      className="border-stone-200 focus:border-[#0F392B] focus:ring-[#0F392B]"
                    />
                  </div>

                  {/* Summary */}
                  <div className="bg-[#F9F9F7] rounded-xl p-6">
                    <h3 className="font-heading text-lg font-semibold text-[#0F392B] mb-4">
                      Appointment Summary
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p className="flex justify-between">
                        <span className="text-[#4A5568]">Service:</span>
                        <span className="font-medium text-[#0F392B]">
                          {taxServices.find((s) => s.id === formData.service)
                            ?.name || formData.service}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-[#4A5568]">Date:</span>
                        <span className="font-medium text-[#0F392B]">
                          {selectedDate && format(selectedDate, "MMMM d, yyyy")}
                        </span>
                      </p>
                      <p className="flex justify-between">
                        <span className="text-[#4A5568]">Time:</span>
                        <span className="font-medium text-[#0F392B]">
                          {formData.time_slot}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="rounded-full px-6 py-3"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-booking-btn"
                      className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        "Booking..."
                      ) : (
                        <>
                          Confirm Appointment
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="font-body text-xs text-[#A0AEC0] text-center">
                    By booking, you agree to our{" "}
                    <a
                      href="/terms"
                      className="text-[#0F392B] hover:underline"
                    >
                      Terms & Conditions
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy"
                      className="text-[#0F392B] hover:underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-[#0F392B] mb-4">
            Need to Reschedule?
          </h2>
          <p className="font-body text-[#4A5568] mb-6">
            Contact us at least 24 hours in advance to change or cancel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:786-991-8919">
              <Button
                variant="outline"
                className="border-2 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-6 py-3"
              >
                <Phone className="w-4 h-4 mr-2" />
                786-991-8919
              </Button>
            </a>
            <a href="mailto:espadywisellc@gmail.com">
              <Button
                variant="outline"
                className="border-2 border-[#0F392B] text-[#0F392B] hover:bg-[#0F392B] hover:text-white rounded-full px-6 py-3"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
